import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CategoryDto } from '../types/categoryTypes';
import type { ProductDto } from '../types/product';
import { searchProducts, getKashrutValues } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { KashrutBadge } from './KashrutBadge';
import { productImageUrl } from '../config';
import '../styles/SearchModal.css';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: CategoryDto[];
}

interface FormState {
  q: string;
  manufacturer: string;
  categoryId: string;
  weightMin: string;
  weightMax: string;
  department: string;
  kashrut: string;
}

const EMPTY_FORM: FormState = {
  q: '', manufacturer: '', categoryId: '', weightMin: '', weightMax: '', department: '', kashrut: '',
};


function formatWeight(w: number): string {
  return w < 1 ? `${Math.round(w * 1000)} גר'` : `${w} ק״ג`;
}

export function SearchModal({ isOpen, onClose, categories }: SearchModalProps): JSX.Element | null {
  const navigate   = useNavigate();
  const { isAdmin } = useAuth();
  const isGuest    = !isAdmin;
  const { cartItems, addToCart, removeFromCart, updateQuantity } = useCart();

  const selectedIds = new Set(cartItems.map((ci) => ci.product.id));

  const [form, setForm]             = useState<FormState>(EMPTY_FORM);
  const [results, setResults]       = useState<ProductDto[] | null>(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [searched, setSearched]     = useState(false);
  const [kashrutList, setKashrutList] = useState<string[]>([]);
  const [zoomImage, setZoomImage]   = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    if (!isOpen || kashrutList.length > 0) return;
    getKashrutValues().then(setKashrutList).catch(() => {});
  }, [isOpen, kashrutList.length]);

  // Flat list: top-level categories + subcategories
  const allCats = categories.flatMap((c) => [
    { id: c.id, label: c.nameHe },
    ...c.subCategories.map((s) => ({ id: s.id, label: `↳ ${s.nameHe}` })),
  ]);

  // Map id → name for result rows
  const catNameMap = new Map<number, string>(
    categories.flatMap((c) => [
      [c.id, c.nameHe] as [number, string],
      ...c.subCategories.map((s) => [s.id, s.nameHe] as [number, string]),
    ])
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function hasFilter(): boolean {
    return !!(form.q.trim() || form.manufacturer.trim() || form.categoryId ||
              form.weightMin || form.weightMax || form.department || form.kashrut);
  }

  async function handleSearch(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!hasFilter()) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const data = await searchProducts({
        q:            form.q.trim() || undefined,
        manufacturer: form.manufacturer.trim() || undefined,
        categoryId:   form.categoryId ? Number(form.categoryId) : undefined,
        weightMin:    form.weightMin  ? Number(form.weightMin)  : undefined,
        weightMax:    form.weightMax  ? Number(form.weightMax)  : undefined,
        department:   form.department || undefined,
        kashrut:      form.kashrut   || undefined,
      });
      setResults(data);
    } catch {
      setError('שגיאה בחיפוש — נסה שוב');
    } finally {
      setLoading(false);
    }
  }

  function handleReset(): void {
    setForm(EMPTY_FORM);
    setResults(null);
    setSearched(false);
    setError(null);
  }

  function toggleSelect(product: ProductDto): void {
    if (selectedIds.has(product.id)) {
      removeFromCart(product.id);
    } else {
      addToCart([product]);
    }
  }

  function toggleSelectAll(list: ProductDto[]): void {
    const allSelected = list.every((p) => selectedIds.has(p.id));
    if (allSelected) list.forEach((p) => removeFromCart(p.id));
    else addToCart(list.filter((p) => !selectedIds.has(p.id)));
  }

  function handleClose(): void {
    handleReset();
    onClose();
  }

  if (!isOpen) return null;

  // Count selected from current results only
  const selectedInResults = results?.filter((p) => selectedIds.has(p.id)) ?? [];

  return (
    <div className="search-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-label="חיפוש מתקדם">
      <div className="search-modal" onClick={(e) => e.stopPropagation()} dir="rtl">

        {/* Header */}
        <div className="search-modal__header">
          <h2 className="search-modal__title">🔍 חיפוש מתקדם</h2>
          <button className="search-modal__close" onClick={handleClose} aria-label="סגור">✕</button>
        </div>

        {/* Search form */}
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-form__row">
            <div className="search-form__field">
              <label className="search-form__label" htmlFor="sf-q">שם מוצר</label>
              <input
                id="sf-q" name="q" type="text"
                className="search-form__input"
                value={form.q}
                onChange={handleChange}
                placeholder="חפש לפי שם..."
                autoFocus
              />
            </div>
            <div className="search-form__field">
              <label className="search-form__label" htmlFor="sf-mfr">יצרן</label>
              <input
                id="sf-mfr" name="manufacturer" type="text"
                className="search-form__input"
                value={form.manufacturer}
                onChange={handleChange}
                placeholder="שם יצרן..."
              />
            </div>
          </div>

          <div className="search-form__row">
            <div className="search-form__field search-form__field--cat">
              <label className="search-form__label" htmlFor="sf-cat">קטגוריה</label>
              <select
                id="sf-cat" name="categoryId"
                className="search-form__input search-form__select"
                value={form.categoryId}
                onChange={handleChange}
              >
                <option value="">כל הקטגוריות</option>
                {allCats.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="search-form__field search-form__field--weight">
              <label className="search-form__label">משקל (ק״ג)</label>
              <div className="search-form__weight-range">
                <input
                  name="weightMin" type="number" step="0.001" min="0"
                  className="search-form__input search-form__input--weight"
                  value={form.weightMin}
                  onChange={handleChange}
                  placeholder="מינימום"
                />
                <span className="search-form__range-sep">—</span>
                <input
                  name="weightMax" type="number" step="0.001" min="0"
                  className="search-form__input search-form__input--weight"
                  value={form.weightMax}
                  onChange={handleChange}
                  placeholder="מקסימום"
                />
              </div>
            </div>
          </div>

          <div className="search-form__row">
            <div className="search-form__field search-form__field--dept">
              <label className="search-form__label">סוג לקוח</label>
              <div className="search-dept-group">
                {(['', 'קמעונאי', 'מוסדי'] as const).map((val) => (
                  <label key={val} className={`search-dept-option${form.department === val ? ' search-dept-option--active' : ''}`}>
                    <input
                      type="radio"
                      name="department"
                      value={val}
                      checked={form.department === val}
                      onChange={handleChange}
                    />
                    {val === '' ? 'הכל' : val}
                  </label>
                ))}
              </div>
            </div>
            <div className="search-form__field search-form__field--dept">
              <label className="search-form__label" htmlFor="sf-kashrut">כשרות</label>
              <select
                id="sf-kashrut"
                name="kashrut"
                className="search-form__input search-form__select"
                value={form.kashrut}
                onChange={handleChange}
              >
                <option value="">כל הכשרויות</option>
                {kashrutList.map((val) => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="search-form__actions">
            <button type="submit" className="search-btn search-btn--primary" disabled={loading || !hasFilter()}>
              {loading ? <span className="search-spinner" /> : '🔍 חפש'}
            </button>
            {(searched || hasFilter()) && (
              <button type="button" className="search-btn search-btn--reset" onClick={handleReset}>
                נקה
              </button>
            )}
          </div>
        </form>

        {/* Results */}
        <div className="search-results">
          {error && (
            <div className="search-results__error">⚠️ {error}</div>
          )}

          {!searched && !loading && (
            <div className="search-results__hint">הזן פרמטרים לחיפוש מוצרים</div>
          )}

          {searched && !loading && results !== null && results.length === 0 && (
            <div className="search-results__empty">לא נמצאו מוצרים התואמים את החיפוש</div>
          )}

          {results !== null && results.length > 0 && (
            <>
              <p className="search-results__count">{results.length} מוצרים נמצאו</p>
              <div className="search-table-wrapper">
                <table className="search-table">
                  <thead>
                    <tr>
                      {isGuest && (
                        <th className="th-checkbox">
                          <input
                            type="checkbox"
                            className="product-checkbox"
                            checked={results.every((p) => selectedIds.has(p.id))}
                            ref={(el) => {
                              if (el) {
                                const some = results.some((p) => selectedIds.has(p.id));
                                const all  = results.every((p) => selectedIds.has(p.id));
                                el.indeterminate = some && !all;
                              }
                            }}
                            onChange={() => toggleSelectAll(results)}
                            aria-label="בחר הכל"
                          />
                        </th>
                      )}
                      {isGuest && <th className="th-qty">כמות</th>}
                      <th>תמונה</th>
                      <th>שם מוצר</th>
                      <th>יצרן</th>
                      <th>משקל</th>
                      <th>כשרות</th>
                      <th>קטגוריה</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((p) => {
                      const isChecked = selectedIds.has(p.id);
                      const cartItem  = cartItems.find((ci) => ci.product.id === p.id);
                      const qty       = cartItem?.quantity ?? 1;
                      return (
                        <tr
                          key={p.id}
                          className={`search-table__row${isGuest && isChecked ? ' search-row--selected' : ''}`}
                          onClick={isGuest ? () => toggleSelect(p) : () => { onClose(); navigate(`/products/${p.categoryId}`); }}
                          style={{ cursor: 'pointer' }}
                        >
                          {isGuest && (
                            <td className="td-checkbox" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="product-checkbox"
                                checked={isChecked}
                                onChange={() => toggleSelect(p)}
                                aria-label={`בחר ${p.nameHebrew}`}
                              />
                            </td>
                          )}
                          {isGuest && (
                            <td className="td-qty" onClick={(e) => e.stopPropagation()}>
                              {isChecked && (
                                <div className="qty-stepper">
                                  <button
                                    className="qty-btn qty-btn--minus"
                                    onClick={() => updateQuantity(p.id, Math.max(1, qty - 1))}
                                    disabled={qty <= 1}
                                    aria-label="הפחת כמות"
                                  >−</button>
                                  <span className="qty-value">{qty}</span>
                                  <button
                                    className="qty-btn qty-btn--plus"
                                    onClick={() => updateQuantity(p.id, qty + 1)}
                                    aria-label="הוסף כמות"
                                  >+</button>
                                </div>
                              )}
                            </td>
                          )}
                          <td>
                            {p.imageUrl ? (
                              <img
                                src={productImageUrl(p.imageUrl)!}
                                alt={p.nameHebrew}
                                className="search-thumbnail search-thumbnail--zoomable"
                                onMouseEnter={() => setZoomImage({ url: productImageUrl(p.imageUrl)!, name: p.nameHebrew })}
                                onMouseLeave={() => setZoomImage(null)}
                                onFocus={() => setZoomImage({ url: productImageUrl(p.imageUrl)!, name: p.nameHebrew })}
                                onBlur={() => setZoomImage(null)}
                                tabIndex={0}
                              />
                            ) : (
                              <div
                                className="search-thumbnail-placeholder search-thumbnail-placeholder--zoomable"
                                onMouseEnter={() => setZoomImage({ url: '/logo.png', name: 'התמונה תתוסף בקרוב' })}
                                onMouseLeave={() => setZoomImage(null)}
                                onFocus={() => setZoomImage({ url: '/logo.png', name: 'התמונה תתוסף בקרוב' })}
                                onBlur={() => setZoomImage(null)}
                                tabIndex={0}
                              >
                                <img src="/logo.png" alt="" className="search-thumbnail-logo" />
                                <span className="search-thumbnail-soon">התמונה תתוסף בקרוב</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <p className="search-name-he">{p.nameHebrew}</p>
                            {p.nameEnglish && <p className="search-name-en">{p.nameEnglish}</p>}
                          </td>
                          <td>{p.manufacturer}</td>
                          <td className="search-td-nowrap">{formatWeight(p.weight)}</td>
                          <td><KashrutBadge kashrut={p.kashrut} /></td>
                          <td className="search-td-cat">
                            {catNameMap.get(p.categoryId) ?? `קטגוריה ${p.categoryId}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Selection bar — guest only */}
        {isGuest && selectedInResults.length > 0 && (
          <div className="search-selection-bar" dir="rtl">
            <span className="search-selection-bar__count">
              🛒 {selectedInResults.length} {selectedInResults.length === 1 ? 'מוצר נבחר' : 'מוצרים נבחרו'}
            </span>
            <button
              className="search-selection-bar__clear"
              onClick={() => selectedInResults.forEach((p) => removeFromCart(p.id))}
            >
              נקה בחירה
            </button>
          </div>
        )}
      </div>
      {zoomImage && (
        <div className="search-img-zoom-overlay" aria-hidden="true">
          <img src={zoomImage.url} alt={zoomImage.name} className="search-img-zoom-img" />
          <p className="search-img-zoom-caption">{zoomImage.name}</p>
        </div>
      )}
    </div>
  );
}
